/**
 * card-video.js
 * Homepage carousel product-card videos — viewport-triggered playback.
 *
 * Flow:
 * 1. Liquid outputs video inside <template> (zero download on page load).
 * 2. IntersectionObserver watches each [data-card-video] wrapper.
 * 3. Card enters viewport → clone template → play → fade in (.is-playing).
 * 4. Card leaves viewport → pause → remove video → fade out (frees memory).
 *
 * Scoped to homepage carousels only via Liquid (janvi_carousel + index page).
 */
(function () {
  // Progressive enhancement — cards still work with images only.
  if (!('IntersectionObserver' in window)) return;

  /**
   * First viewport entry: clone <template> into the wrapper.
   * Later entries: re-clone after prior leave removed the element.
   */
  function getOrInjectVideo(wrapper) {
    let video = wrapper.querySelector('.product-item__video-el');
    if (video) return video;

    const tpl = wrapper.querySelector('template');
    if (!tpl) return null;

    wrapper.appendChild(tpl.content.cloneNode(true));
    return wrapper.querySelector('.product-item__video-el');
  }

  function pauseAndRemoveVideo(wrapper) {
    const video = wrapper.querySelector('.product-item__video-el');
    if (!video) return;

    video.pause();
    video.classList.remove('is-playing');
    video.remove();
  }

  function handlePlayError(err) {
    if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
      console.warn('[card-video] Unexpected play error:', err);
    }
  }

  const observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        const wrapper = entry.target;

        if (entry.isIntersecting) {
          const video = getOrInjectVideo(wrapper);
          if (!video) return;

          // Restart from beginning; .then() adds is-playing on every re-entry.
          video.currentTime = 0;
          video.play().then(function () {
            video.classList.add('is-playing');
          }).catch(handlePlayError);
        } else {
          pauseAndRemoveVideo(wrapper);
        }
      });
    },
    {
      threshold: 0.3, // ~30% of card must be visible
      rootMargin: '0px 0px 50px 0px', // slight preload below the fold
    }
  );

  /** Theme editor: stop observing + remove video when a section is removed. */
  function unobserveWrapper(wrapper) {
    observer.unobserve(wrapper);
    delete wrapper.dataset.cardVideoObserved;
    pauseAndRemoveVideo(wrapper);
  }

  /** Attach observer to each card video wrapper (skip if already observed). */
  function initCardVideos() {
    document.querySelectorAll('[data-card-video]').forEach(function (el) {
      if (el.dataset.cardVideoObserved) return;

      el.dataset.cardVideoObserved = '1';
      observer.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCardVideos);
  } else {
    initCardVideos();
  }

  // Re-init when Shopify theme editor reloads a section.
  document.addEventListener('shopify:section:load', initCardVideos);

  // Clean up when a section is unloaded in the theme editor.
  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-card-video]').forEach(unobserveWrapper);
  });
})();
